import React from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { c } from "../classroomColors";
import { STANDE } from "../standeTokens";
import { Panel, PanelHead, quietButton } from "./parts";
import { plural } from "./spaceTokens";

const Stat = ({ value, label }) => (
  <Box flex="1" bg={c.page} border="1px solid" borderColor={c.borderSoft} borderRadius="12px" px="12px" py="10px">
    <Text fontSize="22px" fontWeight="600" color={c.text} lineHeight="1.2" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Text>
    <Text fontSize="11.5px" color={c.textSub}>
      {label}
    </Text>
  </Box>
);

const OpenButton = ({ onClick, children = "Open" }) => (
  <Button {...quietButton} size="xs" h="28px" px="10px" borderRadius="9px" fontSize="12px" onClick={onClick}>
    {children}
  </Button>
);

/**
 * The work set in the space, at a glance, across the top of the page: what
 * waits to be marked and what is due this week, then the assignments most
 * in need of attention side by side. The full marking view opens from
 * here. A student sees what they still have to do.
 */
export const WorkPanel = ({ work, isInstructor, onOpen, setWork, compact = false }) => (
  <Panel p={{ base: "16px", md: "18px 20px" }}>
    <PanelHead title="Work" action={<Flex gap="6px" align="center">{setWork}<OpenButton onClick={onOpen} /></Flex>} />
    <Box display="grid" gridTemplateColumns={{ base: "minmax(0, 1fr)", md: compact ? "minmax(0, 1fr)" : "280px minmax(0, 1fr)" }} gap="10px" alignItems="stretch">
      <Flex gap="8px">
        <Stat value={isInstructor ? work.toMark : work.toDo} label={isInstructor ? "to mark" : "to do"} />
        <Stat value={work.dueThisWeek} label="due this week" />
      </Flex>
      {work.isLoading ? (
        <Flex align="center" px="4px">
          <Text fontSize="12.5px" color={c.textMuted}>Loading the work…</Text>
        </Flex>
      ) : work.top.length ? (
        <Box display="grid" gridTemplateColumns={{ base: "minmax(0, 1fr)", md: compact ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))" }} gap="8px">
          {work.top.map((a) => {
            const pct = a.total ? Math.round((a.submitted / a.total) * 100) : 0;
            return (
              <Flex key={a.key} direction="column" justify="center" gap="6px" bg={c.page} border="1px solid" borderColor={c.borderSoft}
                borderRadius="12px" px="12px" py="10px" minW={0}>
                <Flex justify="space-between" gap="8px" fontSize="13px">
                  <Text color={c.text} noOfLines={1}>{a.name}</Text>
                  <Text color={c.textMuted} flexShrink={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {isInstructor ? `${a.submitted}/${a.total} in` : a.counts.not_submitted ? "To do" : "Handed in"}
                  </Text>
                </Flex>
                {isInstructor && (
                  <Box h="5px" borderRadius="200px" bg={c.borderSoft} overflow="hidden">
                    <Box h="100%" w={`${pct}%`} bg={STANDE.primaryBlue} borderRadius="200px" />
                  </Box>
                )}
              </Flex>
            );
          })}
        </Box>
      ) : (
        <Flex align="center" px="14px" py="10px" borderRadius="12px" border="1px dashed" borderColor={c.borderSoft}>
          <Text fontSize="12.5px" color={c.textSub}>
            {isInstructor ? "No work set yet. Set a past paper or a custom one, for the space or after a session." : "Nothing set yet."}
          </Text>
        </Flex>
      )}
    </Box>
  </Panel>
);

/** The learning content, unit by unit. The full editor opens from here. */
export const ContentPanel = ({ modules, onOpen }) => (
  <Panel>
    <PanelHead title="Content" count={modules ? modules.length : null} action={<OpenButton onClick={onOpen} />} />
    {modules === null ? (
      <Text fontSize="12.5px" color={c.textMuted}>Loading…</Text>
    ) : modules.length ? (
      <Flex direction="column" gap="6px">
        {modules.slice(0, 4).map((m, i) => (
          <Flex key={m.guid} align="center" gap="10px" px="10px" py="8px" borderRadius="11px" bg={c.page} border="1px solid" borderColor={c.borderSoft}>
            <Flex boxSize="24px" borderRadius="7px" bg={c.surfaceSubtle} align="center" justify="center" fontSize="11px" fontWeight="600" color={c.textSub} flexShrink={0}>
              {i + 1}
            </Flex>
            <Text flex="1" fontSize="13px" color={c.text} noOfLines={1}>{m.name}</Text>
            <Text fontSize="11.5px" color={c.textMuted} flexShrink={0}>
              {plural((m.child_segments || []).length, "lesson")}
            </Text>
          </Flex>
        ))}
        {modules.length > 4 && (
          <Text fontSize="12px" color={c.textMuted} px="4px">and {modules.length - 4} more</Text>
        )}
      </Flex>
    ) : (
      <Text fontSize="12.5px" color={c.textSub}>No content yet. Units and lessons you add show here.</Text>
    )}
  </Panel>
);

/** Who is in the space, and the way to invite and manage them. */
export const PeoplePanel = ({ count, isInstructor, onManage }) => {
  if (!isInstructor) return null;
  return (
    <Panel display="flex" alignItems="center" justifyContent="space-between" gap="10px">
      <Box>
        <Text fontSize="15px" fontWeight="600" color={c.text}>People</Text>
        <Text fontSize="12.5px" color={c.textSub}>
          {count == null ? "Students and invites" : plural(count, "student")}
        </Text>
      </Box>
      <OpenButton onClick={onManage}>Invite and manage</OpenButton>
    </Panel>
  );
};
